import { useRef, ChangeEvent } from "react";
import CustomText from "../atomic/CustomText";
import CustomFileUploaderProps from "../../services/models/customFileUploader.model";
import Button from "../organism/Button";
import CloseIcon from "../../assets/svg/CloseIcon";
import FileIcon from "../../assets/svg/FileIcon";
import utils from "../../services/utils";
import toastMessages from "../../services/utils/helpers/toastMessage";
import dataConstants from "../../constants/dataConstants";
import {
  normalizeUploadFile,
  normalizeUploadFiles,
} from "../../services/utils/helpers/fileNameNormalizer";

export default function FileUploader({
  value,
  onChange,
  name,
  id,
  labelInfo,
  className,
  disabled,
  setFieldTouched,
  approval,
  displayWhen,
  values,
  isMulti = false,
  isMobileTable,
  whiteBg = false,
  getFieldMeta,
}: CustomFileUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fieldName = name ?? labelInfo?.inputId ?? "logo";
  const inputId = id ?? labelInfo?.inputId ?? fieldName;
  const fieldMeta = getFieldMeta?.(fieldName);
  const errorString = fieldMeta?.error;
  const fieldTouched = fieldMeta?.touched;
  const showError = Boolean(errorString && fieldTouched);

  const validateFile = (file: File): boolean => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    const sizeInMB = file.size / 1024 / 1024;

    if (!dataConstants.validTypes.includes(ext ?? "")) {
      toastMessages.error("Invalid file format");
      return false;
    }
    if (sizeInMB > 55) {
      toastMessages.error("File size limit is exceeded");
      return false;
    }
    return true;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setFieldTouched?.(fieldName, true, true);
      return;
    }
    if (!onChange) {
      setFieldTouched?.(fieldName, true, true);
      return;
    }

    const validFiles: File[] = [];

    normalizeUploadFiles(files).forEach((file) => {
      if (validateFile(file)) {
        validFiles.push(file);
      }
    });

    if (validFiles.length === 0) {
      setFieldTouched?.(fieldName, true, true);
      return;
    }

    setFieldTouched?.(fieldName, true, true);

    const updatedFiles = isMulti
      ? [...(Array.isArray(value) ? value : [value]), ...validFiles]
      : validFiles[0];

    onChange({
      target: {
        name: fieldName,
        value: updatedFiles,
        orderId: name,
        filePath: `documents/${validFiles[0]?.name}`,
      },
    });

    event.target.value = ""; // Reset input
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFiles = event.dataTransfer?.files;
    if (!droppedFiles || droppedFiles.length === 0) {
      setFieldTouched?.(fieldName, true, true);
      return;
    }

    const validFiles: File[] = [];

    Array.from(droppedFiles).forEach((file) => {
      if (!(file instanceof File)) return;
      const normalizedFile = normalizeUploadFile(file);
      if (validateFile(normalizedFile)) {
        validFiles.push(normalizedFile);
      }
    });

    if (validFiles.length === 0) {
      setFieldTouched?.(fieldName, true, true);
      return;
    }

    setFieldTouched?.(fieldName, true, true);

    const updatedFiles = isMulti
      ? [...(Array.isArray(value) ? value : []), ...validFiles]
      : validFiles[0];

    onChange?.({
      target: {
        name: fieldName,
        value: updatedFiles,
        orderId: name,
        filePath: `documents/${validFiles[0]?.name}`,
      },
    });
  };

  const handleView = (file: any) => {
    if (file instanceof File) {
      window.open(URL.createObjectURL(file), "_blank");
    } else if (!!file?.container) {
      window.open(file.url, "_blank");
    }
  };

  const handleRemove = (index: number) => {
    if (isMulti && Array.isArray(value)) {
      const updated = [...value];
      updated.splice(index, 1);

      onChange?.({
        target: {
          name: fieldName,
          value: updated,
        },
      });
    } else {
      // Handle single file removal
      onChange?.({
        target: {
          name: fieldName,
          value: null,
        },
      });
    }

    if (inputRef.current) inputRef.current.value = "";
  };

  if (displayWhen && !utils.displayHandler(displayWhen, values ?? {})) {
    return null;
  }

  const fileArray =
    isMulti && Array.isArray(value) ? value : !!value ? [value] : [];

  return (
    <div
      className={`${className} ${isMulti ? "max-w-full" : ""} ${approval ? "flex flex-col w-full max-w-[220px]" : ""}`}
      draggable
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <div className={`${isMulti ? "max-w-[300px]" : ""} ${approval ? "w-full flex flex-col flex-1 min-h-0" : ""}`}>
        <label
          htmlFor={inputId}
          className={`text-xs font-normal block ${showError ? "text-requiredRed" : "text-royalBlue"
            } ${approval ? "min-h-[1.5rem] line-clamp-2 break-words" : ""}`}
        >
          <span className={approval ? "line-clamp-2" : ""}>{labelInfo?.label ?? ""}</span>
          {labelInfo?.required && (
            <span className="text-requiredRed ml-1">*</span>
          )}
        </label>
        <input
          ref={inputRef}
          name={fieldName}
          id={inputId}
          type="file"
          multiple={isMulti}
          accept=".png, .jpeg, .jpg, .pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleInputChange}
          hidden
        />

        {(isMulti || fileArray.length === 0 || approval) && (
          <div
            className={`bg-sideNavGray border-dashed border-roundedInputBorder rounded-[8px] p-3 border-2 mt-2 flex flex-col items-center ${approval ? "min-h-[100px] w-full" : ""}`}
          >
            <h4 className="text-center text-xs">
              Upload or drag and drop file{isMulti ? "s" : ""}
            </h4>
            <Button
              textInformation={{
                text: `Choose File${isMulti ? "(s)" : ""}`,
                fontWeight: "font-semibold",
              }}
              onClick={() => {
                setFieldTouched?.(fieldName, true, true);
                inputRef?.current?.click();
              }}
              disabled={disabled}
              buttonTheme="secondary"
              className="mt-3.5 bg-white p-1.5"
            />
          </div>
        )}
      </div>
      {fileArray.length > 0 && (
        <div
          className={`mt-2 flex flex-wrap w-full ${isMulti ? "flex-row" : "flex-col"
            } gap-2 ${approval ? "min-h-[100px]" : ""}`}
        >
          {fileArray.filter(Boolean).map((file, idx) => (
            <div
              key={idx}
              className={`border rounded-lg ${whiteBg ? "bg-white" : ""
                } shadow-sm p-2 flex justify-between items-center ${isMobileTable ? "bg-white max-w-[230px]" : "md:max-w-[320px] max-w-[200px]"
                } ${approval ? "w-full max-w-full min-h-[40px]" : ""}`}
            >
              <div className="flex items-center w-4/5">
                <FileIcon className="mr-2 w-[24px]" />
                <div className="truncate w-full">
                  <CustomText
                    text={file?.name ?? file?.filename}
                    fontWeight="font-medium"
                    className="text-xs truncate"
                  />
                  {!file?.container && (
                    <CustomText
                      text={`${Math.round(file?.size / 1024)} kb`}
                      fontWeight="font-normal"
                      className="text-[0.625rem]"
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end justify-between h-full">
                {!file?.container && (
                  <CloseIcon
                    onClick={(e) => {
                      handleRemove(idx);
                      e.stopPropagation();
                    }}
                    className="hover:cursor-pointer mb-1"
                  />
                )}
                <p
                  className="text-royalBlue text-xs hover:underline cursor-pointer"
                  onClick={() => handleView(file)}
                >
                  View
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      {showError && (
        <p className="text-xs text-requiredRed mt-1">{errorString}</p>
      )}
    </div>
  );
}
